import { alpha, createTheme, darken, getContrastRatio, lighten, type Theme, type ThemeOptions } from "@mui/material/styles";
import {
  brand,
  dark as darkRoles,
  elevation,
  font,
  green,
  grey,
  light as lightRoles,
  motion,
  orange,
  radius,
  red,
  type ColorRoles,
} from "./design/tokens";

// MultiDoc Console follows the MultiDoc design system (ecm-frontend v0.6.9,
// same port as Multimage): an
// office-suite shell (brand-coloured header, grey nav pane, white cards with
// soft Fluent shadows on a light grey page) in MultiDoc's identity. Token
// values live in src/design/tokens.ts, usage rules in DESIGN.md. Change a
// colour there, never in a component.

export { tokens } from "./design/tokens";

/** The default brand colour when a tenant sets none: MultiDoc blue. */
export const DEFAULT_BRAND = brand[80];

// Values older builds stored as "defaults" (#1A65EE, plus the ECM list). A tenant that never picked a colour carries these, so treat them
// as unset and use the system brand.
const LEGACY_DEFAULTS = ["#1976d2", "#1a65ee", "#2d4e7a", "#0f6cbd", "#ffb020", "#f59e0b", "#a8741a"];

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** A tenant colour, or null when it is missing, malformed or a legacy default. */
export function brandOrNull(value: string | null | undefined): string | null {
  const v = (value ?? "").trim();
  if (!HEX.test(v) || LEGACY_DEFAULTS.includes(v.toLowerCase())) return null;
  return v;
}

// Multimage's accessibility bar is AA *text* contrast (4.5:1): primary and the
// status colours are used as text (links, outlined chips, KPI deltas), not
// only as fills. ECM nudges to 3:1; we nudge to 4.5:1.
const AA = 4.5;

/** Nudge `color` darker (light mode) or lighter (dark mode) until it reaches `min` on `bg`. */
export function legibleOn(color: string, bg: string, isDark: boolean, min = AA): string {
  let c = color;
  for (let i = 0; i < 16 && getContrastRatio(c, bg) < min; i++) {
    c = isDark ? lighten(c, 0.12) : darken(c, 0.12);
  }
  return c;
}

/** White or near-black text, whichever reads better on `bg`. */
function onFill(bg: string): string {
  return getContrastRatio(bg, "#ffffff") >= getContrastRatio(bg, grey[8]) ? "#ffffff" : grey[8];
}

/** A full MUI palette colour from one hex. */
function paletteColor(main: string) {
  return { main, light: lighten(main, 0.25), dark: darken(main, 0.15), contrastText: onFill(main) };
}

/** The roles as CSS custom properties, so plain .css files can use them. */
function cssVars(r: ColorRoles, primary: string, primaryInk: string, accent: string) {
  return {
    "--md-bg": r.bg,
    "--md-surface": r.surface,
    "--md-sunken": r.sunken,
    "--md-hover": r.hover,
    "--md-rule": r.rule,
    "--md-rule-strong": r.ruleStrong,
    "--md-ink": r.ink,
    "--md-ink-secondary": r.inkSecondary,
    "--md-ink-muted": r.inkMuted,
    "--md-nav": r.nav,
    "--md-nav-ink": r.navInk,
    "--md-primary": primary,
    "--md-primary-ink": primaryInk,
    "--md-accent": accent,
    "--md-selection": alpha(primary, 0.2),
    "--md-success": r.success,
    "--md-warning": r.warning,
    "--md-error": r.error,
    "--md-info": r.info,
    "--md-font-sans": font.sans,
    "--md-font-mono": font.mono,
    "--md-radius-sm": `${radius.sm}px`,
    "--md-radius-md": `${radius.md}px`,
    "--md-radius-lg": `${radius.lg}px`,
    "--md-radius-xl": `${radius.xl}px`,
  };
}

export interface ThemeOptionsExtra {
  /** Honour `prefers-reduced-motion`: no ripples, no transitions (UX-5 T1.9). */
  reducedMotion?: boolean;
  /** Tenant highlight colour (branding `accentColor`); falls back to the brand. */
  accent?: string | null;
}

/** A fill on the suite header that moves AWAY from the header's ink (darker
 *  under white text, lighter under dark text), so text on it keeps at least the
 *  header's own contrast. Tinting with the ink itself washes contrast out. */
export function headerWash(theme: Theme, a: number): string {
  const whiteInk = theme.palette.header.contrastText === "#ffffff";
  return alpha(whiteInk ? theme.palette.common.black : theme.palette.common.white, a);
}

/**
 * The dark "terminal" panes that stream job logs and node output. They stay
 * dark in both modes on purpose (a log reads like a console), so their colours
 * come from the neutral and status ramps here, every one AA on `bg`.
 */
export const terminal = {
  bg: grey[8],
  ink: grey[90],
  dim: grey[68],
  error: red[100],
  warning: orange[100],
  ok: green[100],
} as const;

/** Keyboard focus ring token. One place, so every focusable control agrees. */
export const FOCUS_RING = (color: string) => ({
  outline: `2px solid ${color}`,
  outlineOffset: 2,
});

export function createAppTheme(
  mode: "light" | "dark",
  tenantBrand: string | null = null,
  extra: ThemeOptionsExtra = {},
): Theme {
  const isDark = mode === "dark";
  const reduced = Boolean(extra.reducedMotion);
  const r = isDark ? darkRoles : lightRoles;
  const shadow = isDark ? elevation.dark : elevation.light;
  const brandHex = brandOrNull(tenantBrand);
  const accentHex = brandOrNull(extra.accent);

  // Primary is also text (links, subtle buttons, the selected tab), so it must
  // read against both the card and the page.
  const primaryMain = legibleOn(legibleOn(brandHex ?? r.accent, r.surface, isDark), r.bg, isDark);
  const primary = paletteColor(primaryMain);
  const accent = paletteColor(legibleOn(accentHex ?? primaryMain, r.surface, isDark));

  // Suite header: the tenant's brand colour; dark mode uses a neutral bar so
  // the page, not the chrome, stays the focus.
  const headerBg = isDark ? r.headerDark : legibleOn(brandHex ?? lightRoles.accent, "#ffffff", false);
  const headerInk = onFill(headerBg);

  // Status colours double as text on cards and the page.
  const status = (c: string) => legibleOn(legibleOn(c, r.surface, isDark), r.bg, isDark);
  // `text.disabled` doubles as the de-emphasised caption colour on ~50 sites,
  // so it stays readable (≥4.5:1), unlike ECM's decorative inkMuted.
  const textDisabled = isDark ? grey[60] : grey[44];

  const options: ThemeOptions = {
    palette: {
      mode,
      // Pick dark text on any fill that cannot carry white at AA.
      contrastThreshold: 4.5,
      primary,
      accent,
      secondary: {
        main: isDark ? grey[84] : grey[26],
        light: grey[60],
        dark: grey[16],
        contrastText: isDark ? grey[8] : "#ffffff",
      },
      success: paletteColor(status(r.success)),
      warning: paletteColor(status(r.warning)),
      error: paletteColor(status(r.error)),
      info: paletteColor(status(r.info)),
      header: { main: headerBg, light: lighten(headerBg, 0.1), dark: darken(headerBg, 0.15), contrastText: headerInk },
      nav: {
        main: r.nav,
        light: lighten(r.nav, 0.1),
        dark: darken(r.nav, 0.1),
        contrastText: r.navInk,
        muted: r.navInkMuted,
        rule: r.navRule,
        selected: r.navSelected,
      },
      surface: { sunken: r.sunken, hover: r.hover, rule: r.rule, ruleStrong: r.ruleStrong },
      background: { default: r.bg, paper: r.surface },
      text: { primary: r.ink, secondary: r.inkSecondary, disabled: textDisabled },
      divider: r.rule,
      action: {
        hover: r.hover,
        selected: isDark ? alpha(primaryMain, 0.2) : brand[160],
        focus: alpha(primaryMain, 0.12),
      },
      grey: {
        50: grey[98], 100: grey[96], 200: grey[88], 300: grey[82], 400: grey[60],
        500: grey[44], 600: grey[38], 700: grey[26], 800: grey[16], 900: grey[12],
      },
    },
    typography: {
      // Fluent 2 web type ramp. The root font-size is 90% (compact), so these
      // rem values land on Fluent's pixel sizes (14px body, 12px caption…).
      fontFamily: font.sans,
      h1: { fontFamily: font.display, fontWeight: 600, fontSize: "2.2222rem", lineHeight: 1.25 },
      h2: { fontFamily: font.display, fontWeight: 600, fontSize: "1.9444rem", lineHeight: 1.29 },
      h3: { fontFamily: font.display, fontWeight: 600, fontSize: "1.6667rem", lineHeight: 1.33 },
      h4: { fontWeight: 600, fontSize: "1.3889rem", lineHeight: 1.4 },
      h5: { fontWeight: 600, fontSize: "1.1111rem", lineHeight: 1.375 },
      h6: { fontWeight: 600, fontSize: "0.9722rem", lineHeight: 1.43 },
      subtitle1: { fontWeight: 400, fontSize: "1.1111rem", lineHeight: 1.375 },
      subtitle2: { fontWeight: 600, fontSize: "0.9722rem", lineHeight: 1.43 },
      body1: { fontSize: "0.9722rem", lineHeight: 1.43 },
      body2: { fontSize: "0.9028rem", lineHeight: 1.4 },
      caption: { fontSize: "0.8333rem", lineHeight: 1.33 },
      overline: { fontSize: "0.8333rem", fontWeight: 600, lineHeight: 1.33, letterSpacing: 0, textTransform: "none" },
      button: { fontWeight: 600, fontSize: "0.9722rem", textTransform: "none", letterSpacing: 0 },
    },
    shape: { borderRadius: radius.md },
    // MUI indexes shadows 0–24; map them onto Fluent's six depths.
    shadows: [
      "none",
      shadow.s2,
      shadow.s4,
      shadow.s8,
      shadow.s8,
      ...Array(4).fill(shadow.s16),
      ...Array(8).fill(shadow.s28),
      ...Array(8).fill(shadow.s64),
    ] as ThemeOptions["shadows"],
    transitions: reduced
      ? { create: () => "none" }
      : { easing: { easeInOut: "cubic-bezier(0.33, 0, 0.67, 1)", easeOut: "cubic-bezier(0.33, 0, 0.1, 1)" } },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ":root": cssVars(r, primaryMain, primary.contrastText, accent.main),
          html: { fontSize: "90%" },
          body: {
            backgroundColor: r.bg,
            scrollbarWidth: "thin",
            scrollbarColor: `${isDark ? grey[40] : grey[74]} transparent`,
          },
          "code, kbd, samp, pre": { fontFamily: font.mono },
          // Plain <a> and RouterLink text links take the brand link colour, not
          // the browser's #0000EE (which is unreadable in dark mode).
          "a:not([class*='Mui'])": { color: primaryMain },
          // Visible keyboard focus everywhere (WCAG 2.4.7). Mouse users never
          // see it: :focus-visible only matches keyboard/programmatic focus.
          // MUI ButtonBase resets `outline: 0` on its root, and its emotion
          // styles are injected after the baseline, so the selector needs the
          // extra class to outrank it.
          ":focus-visible, .MuiButtonBase-root:focus-visible, .MuiButtonBase-root.Mui-focusVisible": FOCUS_RING(primaryMain),
          // The brand ring is invisible on the brand-coloured header, so it goes
          // to the header's ink there.
          '.MuiAppBar-root :focus-visible, .MuiAppBar-root .Mui-focusVisible, [data-chrome="header"] :focus-visible, [data-chrome="header"] .Mui-focusVisible': {
            outlineColor: headerInk,
          },
          "@media (prefers-reduced-motion: reduce)": {
            "*, *::before, *::after": {
              animationDuration: "0.01ms !important",
              animationIterationCount: "1 !important",
              transitionDuration: "0.01ms !important",
              scrollBehavior: "auto !important",
            },
          },
        },
      },
      MuiButtonBase: { defaultProps: { disableRipple: reduced } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            padding: "5px 12px",
            minHeight: 32,
            minWidth: 64,
            transition: `background-color ${motion.fast}, border-color ${motion.fast}, color ${motion.fast}`,
          },
          sizeSmall: { padding: "3px 8px", minHeight: 24, fontSize: "0.8333rem" },
          sizeLarge: { padding: "8px 16px", minHeight: 40, fontSize: "1.1111rem" },
          contained: { boxShadow: "none", "&:hover": { boxShadow: "none" } },
          outlined: {
            borderColor: r.ruleStrong,
            backgroundColor: r.surface,
            "&:hover": { borderColor: isDark ? grey[60] : grey[74], backgroundColor: r.hover },
          },
          // Fluent's default button is white with a grey border and ink text;
          // keep coloured outlines only where a colour was asked for.
          outlinedPrimary: { color: r.ink },
          text: { "&:hover": { backgroundColor: r.hover } },
        },
      },
      MuiIconButton: { styleOverrides: { root: { borderRadius: radius.md } } },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
          rounded: { borderRadius: radius.xl },
          outlined: { borderColor: r.rule },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 2 },
        styleOverrides: {
          root: { borderRadius: radius.xl, border: isDark ? `1px solid ${r.rule}` : "none" },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: { padding: "12px 16px" },
          title: { fontSize: "0.9722rem", fontWeight: 600 },
          subheader: { fontSize: "0.8333rem" },
        },
      },
      MuiCardContent: {
        styleOverrides: { root: { padding: "12px 16px", "&:last-child": { paddingBottom: 16 } } },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundColor: r.nav, borderRight: `1px solid ${r.navRule}`, borderRadius: 0 },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundColor: headerBg, color: headerInk, borderBottom: "none", boxShadow: "none", borderRadius: 0 },
        },
      },
      MuiTextField: { defaultProps: { size: "small" } },
      MuiOutlinedInput: {
        styleOverrides: {
          // Fluent input: light border, a darker bottom edge, and a
          // brand-coloured underline when focused.
          root: {
            borderRadius: radius.md,
            backgroundColor: r.surface,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: r.ruleStrong,
              borderBottomColor: isDark ? grey[60] : grey[38],
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? grey[60] : grey[74],
              borderBottomColor: isDark ? grey[74] : grey[26],
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderWidth: 1,
              borderColor: r.ruleStrong,
              borderBottom: `2px solid ${primaryMain}`,
            },
            "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: r.error },
          },
        },
      },
      MuiFormHelperText: {
        // Helper text on a disabled field usually explains why it is locked.
        styleOverrides: { root: { "&.Mui-disabled": { color: r.inkSecondary } } },
      },
      MuiInputLabel: {
        styleOverrides: { root: { color: r.inkSecondary, "&.Mui-focused": { color: r.inkSecondary } } },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: radius.md, fontWeight: 600, fontSize: "0.8333rem", height: 24 },
          sizeSmall: { height: 20, fontSize: "0.7639rem" },
          outlined: { borderColor: r.ruleStrong },
          colorDefault: { "&.MuiChip-filled": { backgroundColor: isDark ? grey[24] : grey[94], color: r.ink } },
        },
      },
      MuiTabs: {
        styleOverrides: { root: { minHeight: 40 }, indicator: { height: 3, borderRadius: 2 } },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 400,
            fontSize: "0.9722rem",
            minHeight: 40,
            padding: "8px 12px",
            color: r.inkSecondary,
            "&.Mui-selected": { fontWeight: 600, color: r.ink },
            "&:hover": { color: r.ink },
          },
        },
      },
      MuiDialog: { styleOverrides: { paper: { borderRadius: radius.xl, boxShadow: shadow.s64 } } },
      MuiDialogTitle: {
        styleOverrides: { root: { fontWeight: 600, fontSize: "1.3889rem", lineHeight: 1.4, padding: "20px 24px 8px" } },
      },
      MuiDialogContent: { styleOverrides: { root: { padding: "8px 24px" } } },
      MuiDialogActions: { styleOverrides: { root: { padding: "16px 24px 24px", gap: 8 } } },
      MuiMenu: {
        styleOverrides: {
          paper: { boxShadow: shadow.s16, borderRadius: radius.xl, border: isDark ? `1px solid ${r.rule}` : "none" },
          list: { padding: 4 },
        },
      },
      MuiPopover: { styleOverrides: { paper: { boxShadow: shadow.s16, borderRadius: radius.xl } } },
      MuiMenuItem: {
        styleOverrides: { root: { fontSize: "0.9722rem", minHeight: 32, borderRadius: radius.md, padding: "6px 8px" } },
      },
      MuiTooltip: {
        styleOverrides: {
          // Fluent's default tooltip is a small light card, not a black box.
          tooltip: {
            borderRadius: radius.md,
            fontSize: "0.8333rem",
            fontWeight: 400,
            padding: "5px 11px 7px",
            backgroundColor: r.surface,
            color: r.ink,
            boxShadow: shadow.s8,
          },
          arrow: { color: r.surface },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-head": {
              fontWeight: 600,
              fontSize: "0.9028rem",
              color: r.ink,
              backgroundColor: r.surface,
              borderBottom: `1px solid ${r.rule}`,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${r.rule}`, fontSize: "0.9028rem", padding: "8px 12px" },
          sizeSmall: { padding: "6px 10px" },
        },
      },
      MuiTableRow: {
        styleOverrides: { root: { "&.MuiTableRow-hover:hover": { backgroundColor: r.hover } } },
      },
      MuiAlert: {
        styleOverrides: {
          // Fluent MessageBar: tinted bar, ink text, coloured icon.
          root: { borderRadius: radius.md, border: "1px solid", fontSize: "0.9028rem", color: r.ink, alignItems: "center" },
          standardSuccess: { backgroundColor: r.successBg, borderColor: isDark ? alpha(r.success, 0.4) : green[20], "& .MuiAlert-icon": { color: r.success } },
          standardWarning: { backgroundColor: r.warningBg, borderColor: isDark ? alpha(r.warning, 0.4) : orange[20], "& .MuiAlert-icon": { color: r.warning } },
          standardError: { backgroundColor: r.errorBg, borderColor: isDark ? alpha(r.error, 0.4) : red[20], "& .MuiAlert-icon": { color: r.error } },
          standardInfo: { backgroundColor: isDark ? r.infoBg : grey[98], borderColor: isDark ? r.rule : grey[88], "& .MuiAlert-icon": { color: r.info } },
        },
      },
      MuiCircularProgress: { defaultProps: { "aria-label": "Loading" } },
      MuiLinearProgress: {
        defaultProps: { "aria-label": "Progress" },
        styleOverrides: { root: { borderRadius: 2, backgroundColor: isDark ? grey[24] : grey[88] } },
      },
      MuiAvatar: { styleOverrides: { root: { fontWeight: 600, fontSize: "0.8333rem" } } },
      MuiDivider: { styleOverrides: { root: { borderColor: r.rule } } },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            "&:hover": { backgroundColor: r.hover },
            "&.Mui-selected": {
              backgroundColor: isDark ? alpha(primaryMain, 0.2) : brand[160],
              color: r.ink,
              "&:hover": { backgroundColor: isDark ? alpha(primaryMain, 0.26) : brand[150] },
            },
          },
        },
      },
    },
  };

  return createTheme(options);
}

export type ColorMode = "light" | "dark";

/** The Console's theme: MultiDoc blue, no tenant branding (it is our own
 *  product). `createAppTheme` also takes a brand colour for previews. */
export function buildTheme(mode: ColorMode): Theme {
  return createAppTheme(mode);
}

// Default (light) theme — kept as a named export for existing imports.
export const theme = buildTheme("light");
